import { useEffect, useRef, useState } from 'react'
import { messageFor } from '../lib/messages.ts'

/** How long the word "Copied" stays before the control reads Copy again. */
export const COPIED_RESET_MS = 3_000

/**
 * How long a refusal's sentence stays. Longer than "Copied", because it is a sentence to read and
 * then act on (select the value, copy it by hand) rather than a word to glance at — but still a few
 * seconds, not forever: a refusal left on screen reads as current long after it stopped being true
 * (TAS-21, O4).
 */
export const COPY_REFUSED_RESET_MS = 8_000

/**
 * A Copy control for a value that is **not a secret** — a tenant id, a build key, a slug, a
 * nickname. docs/ux/PLATFORM-ADMIN.md §10.3 (the nickname) and §11.1 (the tenant id).
 *
 * - **It says Copied only after the clipboard write resolves.** The old system said "copied"
 *   whether or not it was (§10.3); here the word changes in the promise's continuation and
 *   nowhere else, so a refusal can never have been announced as a success first.
 * - **A refusal says so, in a sentence chosen by code** (§20.1), and the value stays on screen —
 *   the caller always renders the value beside this control, so selecting it by hand works.
 * - **Neither outcome is left standing** (TAS-21, O4). "Copied" and the refusal both go after a few
 *   seconds, **and at once on the owner's next interaction anywhere on the page** — a click, a tap or a
 *   key — so a refusal never survives a rotation, a tab change or any other act that follows it.
 *   One rule for every Copy control: tenant id, slug, build key, nickname, commits.
 * - **The accessible name carries what is being copied** ("Copy tenant id"), because a page
 *   can hold three of these and "Copy, Copy, Copy" is not a list of controls anybody can use.
 *   It contains the visible word, so a voice-control user saying "click Copy" still lands.
 *   The outcome is announced through a status region, so the control's own name does not
 *   change under a screen reader's cursor.
 *
 * **Never use this for the activation link.** That value is a credential; it has its own
 * control with its own rules in `handoff.tsx` (masked by default, copy without revealing). This
 * component keeps no copy of what it was given beyond the render.
 */
export function CopyButton({
  value,
  noun,
  quiet = false,
}: {
  /** What goes on the clipboard. */
  readonly value: string
  /** What it is, for the accessible name and the announcement: "tenant id", "build key". */
  readonly noun: string
  /** The design's quiet (borderless) style, for a row that already has a lot on it. */
  readonly quiet?: boolean
}) {
  const [outcome, setOutcome] = useState<'idle' | 'copied' | 'refused'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(
    () => () => {
      clearTimeout(timer.current)
    },
    [],
  )

  // While an outcome is showing, the owner's next interaction anywhere ends it. Listening on the
  // document (capture, so a handler that stops propagation cannot hide an interaction from it) is
  // what makes the rule hold for a rotation, a tab change or a click in a different card, none of
  // which this control can see. The listeners exist only while there is something to clear, and the
  // click that started the copy is over before the outcome is set, so it cannot clear its own result.
  //
  // **A click, not a press.** Taking the message away on `pointerdown` shortens the page under the
  // pointer before the button is released, so a control further down the page slides out from under
  // it and the click never arrives — found in the browser, on the Overview, where Rotate sits below
  // the Identity card that holds this message. A `click` has already been delivered to its target
  // when this runs, and the layout shifts after it, not under it.
  useEffect(() => {
    if (outcome === 'idle') {
      return undefined
    }
    const clear = () => {
      clearTimeout(timer.current)
      setOutcome('idle')
    }
    document.addEventListener('click', clear, true)
    document.addEventListener('keydown', clear, true)
    return () => {
      document.removeEventListener('click', clear, true)
      document.removeEventListener('keydown', clear, true)
    }
  }, [outcome])

  const copy = async () => {
    clearTimeout(timer.current)
    let next: 'copied' | 'refused'
    try {
      await navigator.clipboard.writeText(value)
      next = 'copied'
    } catch {
      // The thrown value is deliberately not inspected and never logged: it is the
      // browser's own permission text, and this application renders its own sentence.
      next = 'refused'
    }
    setOutcome(next)
    timer.current = setTimeout(
      () => {
        setOutcome('idle')
      },
      next === 'copied' ? COPIED_RESET_MS : COPY_REFUSED_RESET_MS,
    )
  }

  return (
    <>
      <button
        type="button"
        className={quiet ? 'button button-quiet button-sm' : 'button button-sm'}
        aria-label={`Copy ${noun}`}
        onClick={() => {
          void copy()
        }}
      >
        {outcome === 'copied' ? 'Copied' : 'Copy'}
      </button>
      <span className="visually-hidden" role="status">
        {outcome === 'copied' ? `The ${noun} was copied to the clipboard.` : ''}
      </span>
      {outcome === 'refused' ? (
        <span className="copy-refused" role="alert">
          {messageFor('tenant', 'client_clipboard_denied')}
        </span>
      ) : null}
    </>
  )
}
